import { type ReactNode, useEffect, useMemo, useState } from 'react';

import {
	PreferenceContext,
	type PreferenceContextValue,
	type Preferences,
	type ThemeAccentColor,
	type ThemeAppearance,
	type ThemePanelBackground,
	type ThemeRadius,
	type ThemeScaling,
	defaultPreferences
} from './theme-context';

const allowedThemes = new Set<ThemeAppearance>(['light', 'dark', 'system']);
const allowedAccentColors = new Set<ThemeAccentColor>([
	'default',
	'red',
	'green',
	'orange',
	'gray'
]);
const allowedRadii = new Set<ThemeRadius>(['none', 'small', 'medium', 'large', 'full']);
const allowedScales = new Set<ThemeScaling>(['90%', '95%', '100%', '105%', '110%']);
const allowedPanelBackgrounds = new Set<ThemePanelBackground>(['solid', 'translucent']);

const toTheme = (value: unknown): ThemeAppearance =>
	typeof value === 'string' && allowedThemes.has(value as ThemeAppearance)
		? (value as ThemeAppearance)
		: defaultPreferences.theme;

const toAccentColor = (value: unknown): ThemeAccentColor =>
	typeof value === 'string' && allowedAccentColors.has(value as ThemeAccentColor)
		? (value as ThemeAccentColor)
		: defaultPreferences.accentColor;

const toRadius = (value: unknown): ThemeRadius =>
	typeof value === 'string' && allowedRadii.has(value as ThemeRadius)
		? (value as ThemeRadius)
		: defaultPreferences.radius;

const toScaling = (value: unknown): ThemeScaling =>
	typeof value === 'string' && allowedScales.has(value as ThemeScaling)
		? (value as ThemeScaling)
		: defaultPreferences.scaling;

const toPanelBackground = (value: unknown): ThemePanelBackground =>
	typeof value === 'string' && allowedPanelBackgrounds.has(value as ThemePanelBackground)
		? (value as ThemePanelBackground)
		: defaultPreferences.panelBackground;

const toBoolean = (value: unknown, fallback: boolean): boolean =>
	typeof value === 'boolean' ? value : fallback;

const parsePreferences = (value: unknown): Preferences => {
	if (typeof value !== 'object' || value === null) return defaultPreferences;
	const partial = value as Record<string, unknown>;

	return {
		theme: toTheme(partial.theme),
		reducedMotion: toBoolean(partial.reducedMotion, defaultPreferences.reducedMotion),
		accentColor: toAccentColor(partial.accentColor),
		radius: toRadius(partial.radius),
		scaling: toScaling(partial.scaling),
		panelBackground: toPanelBackground(partial.panelBackground),
		transparency: toBoolean(partial.transparency, defaultPreferences.transparency)
	};
};

const getPreferences = (storageKey: string): Preferences => {
	try {
		const stored = localStorage.getItem(storageKey);
		if (!stored) {
			localStorage.setItem(storageKey, JSON.stringify(defaultPreferences));
			return defaultPreferences;
		}
		const preferences = parsePreferences(JSON.parse(stored));
		localStorage.setItem(storageKey, JSON.stringify(preferences));
		return preferences;
	} catch {
		localStorage.setItem(storageKey, JSON.stringify(defaultPreferences));
		return defaultPreferences;
	}
};

const getResolvedAppearance = (theme: ThemeAppearance): 'light' | 'dark' => {
	if (theme === 'light' || theme === 'dark') return theme;
	return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const applyPreferencesToRoot = (preferences: Preferences) => {
	const root = window.document.documentElement;
	const resolvedAppearance = getResolvedAppearance(preferences.theme);
	const panelBackground = preferences.transparency ? 'translucent' : preferences.panelBackground;

	root.classList.remove('light', 'dark');
	root.classList.add(resolvedAppearance);
	root.classList.toggle('reduce-motion', preferences.reducedMotion);

	root.style.colorScheme = resolvedAppearance;
	root.dataset.theme = preferences.theme;
	root.dataset.appearance = resolvedAppearance;
	if (preferences.accentColor !== 'default') {
		root.dataset.accentColor = preferences.accentColor;
	} else {
		delete root.dataset.accentColor;
	}
	root.dataset.radius = preferences.radius;
	root.dataset.scaling = preferences.scaling;
	root.dataset.panelBackground = panelBackground;
	root.dataset.transparency = preferences.transparency ? 'true' : 'false';
	root.dataset.motion = preferences.reducedMotion ? 'reduced' : 'normal';
};

interface PreferenceProviderProps {
	children: ReactNode;
	storageKey: string;
}

export function PreferenceProvider({ children, storageKey }: PreferenceProviderProps) {
	const [preferences, setPreferences] = useState<Preferences>(() => getPreferences(storageKey));

	useEffect(() => {
		localStorage.setItem(storageKey, JSON.stringify(preferences));
	}, [preferences, storageKey]);

	useEffect(() => {
		applyPreferencesToRoot(preferences);

		if (preferences.theme !== 'system') return;

		const media = window.matchMedia('(prefers-color-scheme: dark)');
		const handleChange = () => applyPreferencesToRoot(preferences);

		media.addEventListener('change', handleChange);
		return () => media.removeEventListener('change', handleChange);
	}, [preferences]);

	const value = useMemo<PreferenceContextValue>(
		() => ({
			preferences,
			setPreferences,
			updatePreferences: (next) => {
				setPreferences((current) => ({ ...current, ...next }));
			}
		}),
		[preferences]
	);

	return <PreferenceContext.Provider value={value}>{children}</PreferenceContext.Provider>;
}

export const ThemeProvider = PreferenceProvider;
